import { animationEnabled } from "components/dashboard";
import { useMemo } from "react";
import { Doughnut } from "react-chartjs-2";
import getCustomLegend from "utils/ChartHelpers.js/CustomLegendPlugin";
import { cn } from "utils/common.utils";

/**
 * Custom Hook to configure Chart.js Doughnut options and return ready-to-use components.
 * This hook generates options immutably and memoizes the result for performance.
 *
 * @param {string} containerId - ID of the container element where the custom HTML legend will be placed.
 * @param {boolean} [isHalfChart=false] - If true, configures the chart for a half-doughnut display (rotation 180 deg).
 * @returns {{
 * DoughnutChart: function(props: {data: object}): JSX.Element,
 * DoughnutChartFooter: function(props: {className?: string}): JSX.Element
 * }} An object containing the configured Chart component and its corresponding legend container element.
 */
export function useDoughnutChart(containerId, isHalfChart = false) {
  // Use useMemo to ensure the options object is only re-created
  // when the dependencies (containerId or isHalfChart) change.
  const options = useMemo(() => {
    // --- 1. Define Base Configuration ---
    const baseCommonOptions = {
      responsive: true,
      maintainAspectRatio: false,
      animation: animationEnabled,
      cutout: "80%",
      radius: "100%",
      borderRadius: 1,
      borderJoinStyle: "miter",
      borderWidth: 0,
      spacing: 0,
      borderAlign: "center",
    };

    const basePlugins = {
      htmlLegend: {
        containerID: containerId,
      },
      legend: {
        display: false,
      },
      // Base tooltip setting, safely checking document direction
      tooltip: {
        rtl:
          typeof document !== "undefined" && document.body.dir === "rtl"
            ? true
            : false,
      },
    };

    // --- 2. Create Initial Options Object (Full Copy) ---
    let finalOptions = {
      ...baseCommonOptions,
      plugins: { ...basePlugins },
    };

    // --- 3. Apply Half-Chart Specific Overrides (Immutable Modification) ---
    if (isHalfChart) {
      finalOptions.rotation = -90;
      finalOptions.circumference = 180;

      finalOptions.cutout = "85%";
      finalOptions.radius = "100%";

      // Deeply clone and override the tooltip filter
      finalOptions.plugins = {
        ...finalOptions.plugins,
        tooltip: {
          ...finalOptions.plugins.tooltip,
          filter: function (item) {
            return item?.dataIndex === 0;
          },
        },
      };
    }

    return finalOptions;
  }, [containerId, isHalfChart]);

  return {
    DoughnutChart: ({ data }) => (
      <Doughnut data={data} options={options} plugins={[getCustomLegend()]} />
    ),
    DoughnutChartFooter: ({ className }) => (
      <div id={containerId} className={cn("mt-8", className)} />
    ),
  };
}
